import { FormItem, Input, Select, Switch } from 'formik-antd';
import React, { useCallback, useRef, useState } from 'react';
import { Button, DatePicker, Row, Upload } from 'antd';
import clsx from 'clsx';
import { UploadOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import dayjs from '@/lib/dayjsExtended';
import {
  TYPE_BOOLEAN,
  TYPE_INTEGER,
  TYPE_DOUBLE,
  TYPE_DICTIONARY,
  TYPE_ARRAY,
  TYPE_STRING,
  TYPE_IMAGE,
  TYPE_COLOR,
  TYPE_DATE,
  TYPE_SELECT,
  TYPE_JSON,
} from '../../../TemplateBuilder/const/templates';
import isFieldRequired from '../../utils/isFieldRequired';
import useDictionariesOptions from '../../hooks/useDictionariesOptions';
import ColorPicker from '../../../../components/ColorPicker';
import EmptyDictionariesListAlert from '../../../Translations/components/EmptyDictionariesListAlert';
import { DATE_FORMAT } from '../../../../const/system';
import { IntlMessages } from '@/components';
import styles from './DynamicFormField.module.css';

const DynamicFormField = ({ field, formikProps }) => {
  const [dictionaryOptions, isFetching] = useDictionariesOptions(field);
  const [datePickerValue, setDatePickerValue] = useState(null);
  const uploadRef = useRef(null);
  const focusHandlerRef = useRef(null);
  const language = useSelector(({ customise: { language } }) => language);

  const fieldName = field.full_name;
  const fieldLabel = field[`display_name_${language}`];
  const isRequired = isFieldRequired(field?.validations?.data);
  const { setFieldValue, setFieldTouched, values, validateField, setFieldError } = formikProps;

  const handleChange = (event) => {
    const def = isBoolean(event.target.value) ? false : null;

    setFieldValue(`data.${fieldName}`, {
      value: event.target.value ? event.target.value : def,
      template_field_id: field.id,
    });
  };

  const validateEmptyRequiredField = useCallback(
    (delay = 200) => {
      setTimeout(() => {
        const currentFiles = values.data[fieldName]?.value || [];

        if (currentFiles.length === 0 && isRequired) {
          setFieldTouched(`data.${fieldName}.value`, true);
          if (validateField) {
            validateField(`data.${fieldName}.value`);
          }
        }
      }, delay);
    },
    [fieldName, values.data, isRequired, setFieldTouched, validateField],
  );

  const handleFocusAfterFileDialog = useCallback(() => {
    validateEmptyRequiredField(400);
    window.removeEventListener('focus', focusHandlerRef.current);
    focusHandlerRef.current = null;
  }, [validateEmptyRequiredField]);

  const handleUploadClick = useCallback(() => {
    focusHandlerRef.current = handleFocusAfterFileDialog;

    window.addEventListener('focus', focusHandlerRef.current);
  }, [handleFocusAfterFileDialog]);

  const searchDictionaries = useCallback(
    (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    [],
  );

  const validateRequiredField = (value) => {
    if (isRequired) {
      const isEmptyFieldValue = isNil(value) || (Array.isArray(value) && value.length === 0);

      if (isEmptyFieldValue) {
        if (field.type === TYPE_BOOLEAN) {
          return false;
        }

        if (field.type === TYPE_IMAGE) {
          const fieldTouched = formikProps.touched?.data?.[fieldName]?.value;
          const hasUploadInProgress =
            Array.isArray(value) && value.some((file) => file.status === 'uploading');

          if (!fieldTouched || hasUploadInProgress) {
            return true;
          }
        }

        return <IntlMessages id="ui-general-validation-required" />;
      }
    }
    return false;
  };

  const label = (
    <Row className="w-full" justify="start" align="middle">
      <div>{fieldLabel}</div>
    </Row>
  );

  let element = null;

  const hint_key = `hint_${language}`;
  const currentDateFieldValue = field.type === TYPE_DATE ? values.data[fieldName]?.value : null;
  const currentDatePickerValue = currentDateFieldValue ? dayjs(currentDateFieldValue, DATE_FORMAT) : null;

  switch (field.type) {
    case TYPE_BOOLEAN:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <Switch
            checkedChildren="True"
            unCheckedChildren="False"
            name={`data.${fieldName}.value`}
            onChange={(value) => {
              setFieldValue(`data.${fieldName}`, {
                value,
                template_field_id: field.id,
              });
            }}
          />
        </FormItem>
      );
      break;
    case TYPE_INTEGER:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <Input
            required={isRequired}
            type="number"
            name={`data.${fieldName}.value`}
            onChange={handleChange}
          />
        </FormItem>
      );
      break;
    case TYPE_DOUBLE:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <Input required={isRequired} type="text" name={`data.${fieldName}.value`} onChange={handleChange} />
        </FormItem>
      );
      break;
    case TYPE_DICTIONARY:
      element = (
        <>
          <EmptyDictionariesListAlert isLoading={isFetching} options={dictionaryOptions} />

          <FormItem
            label={label}
            extra={field[hint_key]}
            className={clsx('ant-form-item-col', styles.stretched)}
            name={`data.${fieldName}.value`}
            validate={validateRequiredField}
            required={isRequired}
          >
            <Select
              showSearch={{ filterOption: searchDictionaries }}
              allowClear
              name={`data.${fieldName}.value`}
              onChange={(value) => {
                setFieldValue(`data.${fieldName}`, {
                  template_field_id: field.id,
                  value: value || null,
                });
              }}
              options={dictionaryOptions}
              loading={isFetching}
            />
          </FormItem>
        </>
      );
      break;
    case TYPE_ARRAY:
      element = (
        <FormItem
          label={label}
          extra={`${field[hint_key] || ''} (To add new item press Enter)`}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <Select
            allowClear
            name={`data.${fieldName}.value`}
            mode="tags"
            className="w-full none"
            onChange={(value) => {
              setFieldValue(`data.${fieldName}`, {
                template_field_id: field.id,
                value: value || null,
              });
            }}
            onBlur={(event) => {
              if (!event.target.value) {
                return;
              }

              const currentValues = values.data[fieldName]?.value ?? [];

              setFieldValue(`data.${fieldName}.value`, uniq([...currentValues, event.target.value]), true);
            }}
            options={[]}
            dropdownStyle={{ display: 'none' }}
          />
        </FormItem>
      );
      break;
    case TYPE_STRING:
    case TYPE_JSON:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          {field.type === TYPE_JSON ? (
            <Input.TextArea
              required={isRequired}
              name={`data.${fieldName}.value`}
              onChange={handleChange}
              rows={4}
            />
          ) : (
            <Input
              required={isRequired}
              type="text"
              name={`data.${fieldName}.value`}
              onChange={handleChange}
            />
          )}
        </FormItem>
      );
      break;
    case TYPE_IMAGE:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <Upload
            ref={uploadRef}
            beforeUpload={() => false}
            listType="picture"
            fileList={values.data[fieldName]?.value || []}
            onChange={({ fileList }) => {
              if (fileList.length > 1) {
                fileList = fileList.slice(-1);
              }
              setFieldTouched(`data.${fieldName}.value`, true);
              setFieldValue(`data.${fieldName}`, {
                value: fileList,
                template_field_id: field.id,
              });
            }}
            onRemove={() => setFieldTouched(`data.${fieldName}.value`, true)}
          >
            <div>
              <Button
                icon={<UploadOutlined className="hp-mr-4" />}
                onClick={handleUploadClick}
                onBlur={() => validateEmptyRequiredField(400)}
              >
                <IntlMessages id="ui-general-upload-image" />
              </Button>
            </div>
          </Upload>
        </FormItem>
      );
      break;
    case TYPE_COLOR:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className="ant-form-item-col"
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <ColorPicker
            name={`data.${fieldName}.value`}
            fieldName={fieldName}
            defaultValue={values.data[fieldName]?.value || field?.default_value}
            setFieldError={setFieldError}
            onChange={(value) => {
              setFieldValue(`data.${fieldName}`, {
                value,
                template_field_id: field.id,
              });
            }}
          />
        </FormItem>
      );
      break;
    case TYPE_DATE:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <DatePicker
            allowClear
            showToday
            name={`data.${fieldName}.value`}
            format={DATE_FORMAT}
            value={currentDatePickerValue}
            pickerValue={datePickerValue || currentDatePickerValue || undefined}
            onPanelChange={(date) => {
              setDatePickerValue(date);
            }}
            onOpenChange={(open) => {
              setDatePickerValue(open ? currentDatePickerValue : null);
            }}
            onChange={async (date, dateString) => {
              const value = date ? date.format(DATE_FORMAT) : dateString || null;

              await setFieldTouched(`data.${fieldName}.value`, true);

              await setFieldValue(`data.${fieldName}`, {
                value,
                template_field_id: field.id,
              });

              setDatePickerValue(date);
            }}
            onBlur={async () => {
              await setFieldTouched(`data.${fieldName}.value`, true);
            }}
            onClear={async () => {
              await setFieldTouched(`data.${fieldName}.value`, true);
              setDatePickerValue(null);
              await setFieldValue(`data.${fieldName}`, {
                value: null,
                template_field_id: field.id,
              });
            }}
          />
        </FormItem>
      );
      break;
    case TYPE_SELECT:
      element = (
        <FormItem
          label={label}
          extra={field[hint_key]}
          className={clsx('ant-form-item-col', styles.stretched)}
          name={`data.${fieldName}.value`}
          validate={validateRequiredField}
          required={isRequired}
        >
          <Select
            allowClear
            name={`data.${fieldName}.value`}
            className="w-full"
            onChange={(value) => {
              setFieldValue(`data.${fieldName}`, {
                template_field_id: field.id,
                value: value || null,
              });
            }}
            options={field?.value ? JSON.parse(field.value) : []}
          />
        </FormItem>
      );
      break;
    default:
      break;
  }

  return element;
};

DynamicFormField.propTypes = {
  field: PropTypes.object.isRequired,
  formikProps: PropTypes.object.isRequired,
};

export default DynamicFormField;
